import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import {
  ConnectionsSlice,
  createConnectionsSlice,
  initialConnectionsState,
} from 'app/connections/infrastructure'
import {
  ObjectivesSlice,
  createObjectivesSlice,
  initialObjectivesState,
} from 'app/objectives/infrastructure'
import { SignalsSlice, createSignalsSlice, initialSignalsState } from 'app/signals/infrastructure'
import { UserSlice, createUserSlice, initialUserState } from 'app/user/infrastructure'

import createApplicationSlice, {
  ApplicationSlice,
  initialApplicationState,
} from './createApplicationSlice'

export const storageKey = 'application-storage'

interface ResetAllActions {
  resetAll: () => void
}

type ApplicationStorage = ApplicationSlice &
  ResetAllActions &
  ConnectionsSlice &
  ObjectivesSlice &
  SignalsSlice &
  UserSlice

const useApplicationStore = create<ApplicationStorage>()(
  persist(
    (...a) => ({
      ...createApplicationSlice(...a),
      ...createConnectionsSlice(...a),
      ...createObjectivesSlice(...a),
      ...createSignalsSlice(...a),
      ...createUserSlice(...a),
      resetAll: () => {
        const set = a[0]
        set({
          ...initialApplicationState,
          ...initialConnectionsState,
          ...initialObjectivesState,
          ...initialSignalsState,
          ...initialUserState,
        })
      },
    }),
    {
      name: storageKey,
      merge: (persistedState, currentState) => {
        const mergedState = { ...currentState }
        // JSON parsing creates new references for every slice. Keep unchanged
        // slices stable so another tab's update does not restart fetch effects.
        for (const key of Object.keys(currentState) as (keyof ApplicationStorage)[]) {
          const value = (persistedState as Partial<ApplicationStorage> | undefined)?.[key]
          if (
            value !== undefined &&
            typeof currentState[key] !== 'function' &&
            JSON.stringify(value) !== JSON.stringify(currentState[key])
          ) {
            Object.assign(mergedState, { [key]: value })
          }
        }
        return mergedState
      },
    },
  ),
)

export default useApplicationStore
